import Pagination from "@components/Pagination/Pagination";
import React from "react";
import AddNewQuestion from "./AddNewQuestion";
import QuestionsItem from "./QuestionsItem";
import ReviewsAddNewReview from "./ReviewsAddNewReview";
import ReviewsScoreList from "./ReviewsScoreList";
import ReviewsScoreSummary from "./ReviewsScoreSummary";
import { withReviews } from "./withReviews";
import {
  ReviewCard,
  FormRangeSlider,
  TabbedSectionProps,
  TabbedSection,
  FormInput,
  IconSearch,
  FormSelect,
} from "@bared/ui";

export const Reviews = withReviews(
  ({
    averageScore,
    fitFeedbackStatistic,
    gender,
    handleClearReviewFilter,
    handleClickReviewFilter,
    handleLoadMoreQuestions,
    handleLoadMoreReviews,
    handleQuestionsSortOptionChange,
    handleReviewsSortOptionChange,
    handleSearch,
    handleSearchIconClick,
    handleTabChange,
    loading,
    productGroup,
    productUrl,
    questions,
    questionsSortOptions,
    questionsPerPage,
    reviewFilter,
    reviews,
    reviewsPerPage,
    reviewsSortOptions,
    reviewsSummary,
    sku,
    starDistribution,
    siblingColours,
    totalQuestions,
    totalReviews,
    totalPageCount,
    totalQuestionPageCount,
    totalResultsCount,
  }) => {
    const tabs: TabbedSectionProps["tabs"] = [
      { title: `All Reviews (${totalReviews})`, handle: "reviews" },
      { title: `All Questions (${totalQuestions})`, handle: "questions" },
    ];

    return (
      <section className="relative py-maxi" id="reviews">
        {loading ? (
          <p className="text-body leading-hint">Loading...</p>
        ) : totalReviews === 0 && totalQuestions === 0 ? (
          <div className="flex flex-col space-y-midi md:flex-row md:justify-between md:space-y-zero">
            <div className="flex flex-col">
              <h5 className="text-h5 tracking-heading">REVIEWS / QUESTIONS</h5>
              <p className="text-body leading-hint">
                Be the first to leave a review or ask us anything!
              </p>
            </div>

            <div className="flex flex-col space-y-mini md:flex-row md:space-x-mini md:space-y-zero">
              {totalReviews === 0 ? (
                <ReviewsAddNewReview
                  sku={sku}
                  gender={gender}
                  noReview={true}
                />
              ) : null}
              {totalQuestions === 0 ? (
                <AddNewQuestion sku={sku} productUrl={productUrl} />
              ) : null}
            </div>
          </div>
        ) : null}

        {totalReviews > 0 || totalQuestions > 0 ? (
          <div className="relative" id="reviews-questions">
            <TabbedSection tabs={tabs} onChange={() => handleTabChange()}>
              {(tabhandle) => {
                if ("reviews" === tabhandle) {
                  return totalReviews > 0 ? (
                    <div className="pt-mini">
                      <div className="flex justify-between">
                        <ReviewsScoreSummary
                          averageScore={averageScore}
                          productGroup={productGroup}
                        />
                        <ReviewsAddNewReview
                          className="min-w-[190px] px-mini sm:px-maxi"
                          sku={sku}
                          gender={gender}
                        />
                      </div>

                      <div className="space-y-maxi border-b-[1px] border-b-primary-300 py-midi">
                        <div className="flex flex-col md:flex-col lg:flex-row">
                          {/* {reviewsSummary ? <>{reviewsSummary}</> : null} Hiding the AI Summary for now, as the team isn't comfortable with releasing it yet */}

                          <div className="flex flex-col space-y-mini md:flex-row md:space-x-midi md:space-y-zero">
                            <ReviewsScoreList
                              className="md:min-w-[330px] md:max-w-[330px]"
                              reviewFilter={reviewFilter}
                              totalReviews={totalReviews}
                              starDistribution={starDistribution}
                              handleClick={handleClickReviewFilter}
                              handleClear={handleClearReviewFilter}
                            />
                            {gender === "Womens" || gender === "Mens" ? (
                              <div className="flex w-full flex-col space-y-maxi sm:min-w-[330px]">
                                {fitFeedbackStatistic.map((stats) => {
                                  return (
                                    <div className="flex flex-col space-y-mini">
                                      <p className="text-caption font-medium leading-caption">
                                        {stats.title}:
                                      </p>
                                      <FormRangeSlider
                                        rangeOption={stats.rangeOptions}
                                        value={stats.averageRating}
                                        min={1}
                                        max={5}
                                        step={0.01}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="flex flex-col space-y-mini md:flex-row md:items-center md:justify-between md:space-y-zero">
                          <div className="flex space-x-mini">
                            <div className="relative w-1/2 md:min-w-[215px]">
                              <FormInput
                                id="search-keyword"
                                placeholder="Search Reviews"
                                className="h-[41px] border pl-micro pr-maxi text-caption placeholder-primary-600 sm:pl-mini"
                                onKeyDown={handleSearch}
                              />
                              <button
                                className="absolute right-zero top-zero flex h-[40px] w-[30px] cursor-pointer items-center justify-center"
                                onClick={handleSearchIconClick}
                              >
                                <IconSearch className="w-mini" />
                              </button>
                            </div>

                            <div className="w-1/2 md:min-w-[215px]">
                              <FormSelect
                                className="border text-caption"
                                placeholder=""
                                defaultValue={reviewsSortOptions[0].value}
                                options={reviewsSortOptions}
                                onChange={handleReviewsSortOptionChange}
                              />
                            </div>
                          </div>

                          <p className="text-right text-caption leading-caption">
                            {reviewsPerPage} of {totalResultsCount} Reviews
                          </p>
                        </div>
                      </div>

                      <div data-section-id="reviews-container">
                        <div>
                          {loading ? (
                            <p className="pt-midi text-body leading-hint">
                              Loading ...
                            </p>
                          ) : (
                            reviews.map(
                              (
                                {
                                  id,
                                  rating,
                                  title,
                                  comments,
                                  author: { name, location },
                                  productName,
                                  timeAgo,
                                  replies,
                                },
                                index,
                              ) => {
                                const shoeSize =
                                  productName.split(" / ")[1] || undefined;

                                const styleName = siblingColours
                                  ?.filter((text) => {
                                    const regex = /-/g;
                                    return !text.match(regex);
                                  })
                                  .find((name) => productName.includes(name));

                                const emptyLocation = location.replace(
                                  '""',
                                  "",
                                );
                                const country =
                                  location.split(",").length > 1
                                    ? location.split(",")[1]
                                    : location;

                                return (
                                  <ReviewCard
                                    key={id}
                                    userName={name}
                                    country={
                                      !!emptyLocation ? country : undefined
                                    }
                                    shoeStyle={styleName}
                                    shoeSize={shoeSize}
                                    rating={rating}
                                    timeAgo={timeAgo}
                                    reviewTitle={title}
                                    review={comments}
                                    response={replies?.[0]?.comments}
                                    isLast={index + 1 === reviews.length}
                                  />
                                );
                              },
                            )
                          )}

                          {!loading &&
                          totalReviews !== 0 &&
                          reviews.length === 0 ? (
                            <p className="pt-midi text-body leading-hint">{`No ${reviewFilter ? `${reviewFilter}-star ` : ""}reviews found. `}</p>
                          ) : null}
                        </div>

                        {reviews.length !== 0 && (
                          <Pagination
                            pageCount={totalPageCount}
                            pageRangeDisplayed={3}
                            marginPagesDisplayed={1}
                            onPageChange={handleLoadMoreReviews}
                            loading={loading}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex w-full justify-end">
                      <ReviewsAddNewReview
                        className="w-full md:max-w-[190px]"
                        sku={sku}
                        gender={gender}
                      />
                    </div>
                  );
                }

                if ("questions" === tabhandle) {
                  return totalQuestions > 0 ? (
                    <div className="pt-mini">
                      <div className="flex w-full flex-col items-start justify-between space-y-midi md:flex-row md:items-center md:space-y-zero">
                        <AddNewQuestion
                          className="w-full md:order-last md:max-w-[190px]"
                          sku={sku}
                          productUrl={productUrl}
                        />

                        <div className="flex w-full items-center space-x-mini">
                          <div className="w-1/2 md:max-w-[215px]">
                            <FormSelect
                              className="border text-caption"
                              placeholder=""
                              defaultValue={questionsSortOptions[0].value}
                              options={questionsSortOptions}
                              onChange={handleQuestionsSortOptionChange}
                            />
                          </div>

                          <p className="text-caption leading-caption">
                            {questionsPerPage} of {totalQuestions} Questions
                          </p>
                        </div>
                      </div>

                      <div data-section-id="questions-container">
                        <div>
                          {loading ? (
                            <p className="text-body leading-hint">
                              Loading ...
                            </p>
                          ) : (
                            questions.map(
                              ({
                                id,
                                author: { name },
                                comments,
                                timeAgo,
                                replies,
                              }) => {
                                return (
                                  <QuestionsItem
                                    className="last:mb-4 border-b-[1px] border-b-primary-300 py-midi"
                                    key={id}
                                    userName={name}
                                    question={comments}
                                    timeAgo={timeAgo}
                                    responses={replies}
                                  />
                                );
                              },
                            )
                          )}
                        </div>

                        {questions.length !== 0 && (
                          <Pagination
                            pageCount={totalQuestionPageCount}
                            pageRangeDisplayed={3}
                            marginPagesDisplayed={1}
                            onPageChange={handleLoadMoreQuestions}
                            loading={loading}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex w-full flex-col items-start justify-between space-y-micro pt-mini md:flex-row md:items-center md:space-y-zero">
                      <p className="text-body">
                        Leave a review or ask us anything.
                      </p>
                      <AddNewQuestion
                        className="w-full md:max-w-[190px]"
                        sku={sku}
                        productUrl={productUrl}
                      />
                    </div>
                  );
                }
              }}
            </TabbedSection>
          </div>
        ) : null}
      </section>
    );
  },
);
